import { Outlet, useLocation, useNavigation } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils.ts'
import Navigation from '@/components/common/menu/Navigation.tsx'
import BottomTabBar from '@/components/common/menu/BottomTabBar.tsx'
import MobileFab from '@/components/common/menu/MobileFab.tsx'
import LoadingIndicator from '@/components/common/LoadingIndicator.tsx'
import SuspenseWrapper from '@/components/common/wrappers/SuspenseWrapper.tsx'
import PrefetchProgressBar from '@/components/common/PrefetchProgressBar.tsx'
import OfflineBanner from '@/components/common/OfflineBanner.tsx'
import SlowConnectionPrompt from '@/components/common/SlowConnectionPrompt'
import MaintenanceOverrideBanner from '@/components/common/MaintenanceOverrideBanner'
import BackgroundSyncIndicator from '@/components/common/BackgroundSyncIndicator.tsx'
import { useIsBackgroundSyncing } from '@/lib/hooks/useIsBackgroundSyncing'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { useNativeOnlineStatus } from '@/lib/hooks/useNativeOnlineStatus'
import { useOfflineModeStore } from '@/store/useOfflineModeStore'
import { usePullToRefresh } from '@/lib/hooks/usePullToRefresh'
import PullToRefreshIndicator from '@/components/common/PullToRefreshIndicator'
import { usePrefetchStore } from '@/store/usePrefetchStore'
import { useMaintenanceStatus } from '@/lib/hooks/useMaintenanceStatus.tsx'
import { useUserStore } from '@/store/user'
import { getTopBannerContentOffsetPx } from '@/routes/root/view/topBannerLayout.ts'
import PushPromptCard from '@/routes/root/view/PushPromptCard.tsx'
import LicenceBanner from '@/components/common/licence/LicenceBanner.tsx'
import { usePushPromptGate } from '@/lib/push/usePushPromptGate'
import { usePushAutoRegister } from '@/lib/push/usePushAutoRegister'
import { useCameraPreviewGlobalCleanup } from '@/components/common/camera/lib/useCameraPreviewGlobalCleanup'
import useMediaObjectRotationSubscriber from '@/lib/mercure/useMediaObjectRotationSubscriber'
import { usePresenceHeartbeat } from '@/lib/presence/usePresenceHeartbeat'

export default function RootLayout() {
  const isNavigating = useNavigation().state === 'loading'
  const isNative = isNativePlatform()
  const isOnline = useNativeOnlineStatus()
  const isOfflineModeEnabled = useOfflineModeStore(s => s.isOfflineModeEnabled)
  const prefetchPhase = usePrefetchStore(s => s.phase)
  const isPrefetchBannerVisible = usePrefetchStore(s => s.isVisible)
  // The only banner that claims a row of the app's chrome is a PERSISTENT
  // STATE the user is in — genuinely offline, or offline mode switched on.
  // Transient work (background sync) and suggestions (slow connection) were
  // demoted out of this stack; they render as a 2px hairline and a bottom
  // card respectively. The stack is now at most three rows — maintenance,
  // state, prefetch — and the three-row case needs a maintenance-override
  // user mid-download; for everyone else it is at most two. It used to be
  // four for an ordinary user.
  const showOfflineBanner = (isNative && !isOnline) || (isOnline && isOfflineModeEnabled)
  const showStatusBanner = showOfflineBanner
  // Mirrors PrefetchProgressBar's own render gate — must stay in sync, else
  // the offset reserves a banner's worth of space for a banner that's not
  // actually rendering (phantom gap between topbar and content).
  const showPrefetchBanner =
    isPrefetchBannerVisible && prefetchPhase !== 'idle' && prefetchPhase !== 'cancelled'
  const isBackgroundSyncing = useIsBackgroundSyncing()
  // Team-only override banner sits at the top of the stack; regular users get
  // the full-screen MaintenanceScreen (mounted in GlobalLayout) instead.
  const { isMaintenance } = useMaintenanceStatus()
  const isSpecial = useUserStore(
    s => (s.user as { isSpecialUser?: boolean } | null)?.isSpecialUser === true,
  )
  const showMaintenanceBanner = isMaintenance && isSpecial
  const maintenanceOffset = showMaintenanceBanner ? 1 : 0
  const topBannerOffsetPx = getTopBannerContentOffsetPx({
    hasMaintenanceBanner: showMaintenanceBanner,
    hasStatusBanner: showStatusBanner,
    hasPrefetchBanner: showPrefetchBanner,
  })
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const {
    state: pullState,
    contentRef,
    indicatorRef,
    iconWrapperRef,
    handlers,
  } = usePullToRefresh({
    enabled: isNative,
    scrollContainerRef,
  })

  // Defensive: on every app resume, tear down any orphan
  // camera-preview UIView. The native plugin attaches its preview
  // above the WebView and doesn't always clean up if iOS suspends JS
  // mid-stop. Testers reported an invisible overlay blocking touches
  // in the middle of the screen after backgrounding + returning to
  // the app — that overlay is the leaked native view. Idempotent
  // no-op when no preview is alive.
  useCameraPreviewGlobalCleanup()

  // Listen for `/users/{uid}/bg_sync/media_objects/rotate/*` echoes so the
  // lightbox optimistic state in `useLightboxRotationStore` clears
  // after the server confirms a rotate landed. Mounted here (not
  // inside GalleryLazy) so the subscription survives lightbox
  // close — the Mercure echo can arrive seconds after the user
  // dismissed the lightbox, and we still need to drop the optimistic
  // blob URL when it does.
  useMediaObjectRotationSubscriber()

  // Throttled presence ping → User.lastSeenAt, for "push only when away" gating.
  usePresenceHeartbeat()

  // Decides whether the post-login push soft prompt may appear. Never requests
  // OS permission itself — only a tap on the card's Enable button does that.
  // Reuses this component's online subscription instead of opening a second one,
  // and treats deliberate offline mode as offline: enabling push POSTs
  // /push_devices, which is not a queueable endpoint, so offering it there would
  // hand the user a button that can only fail.
  usePushPromptGate({ isOnline: isOnline && !isOfflineModeEnabled })
  // Re-establish a registration this device already earned before a logout
  // removed it. Runs beside the prompt gate rather than inside it: once this
  // succeeds the gate returns `already-registered` on its own, so the card
  // stops appearing without either hook knowing about the other.
  usePushAutoRegister({ isOnline: isOnline && !isOfflineModeEnabled })

  const { pathname } = useLocation()
  useEffect(() => {
    if (pathname) {
      scrollContainerRef.current?.scrollTo(0, 0)
    }
  }, [pathname])

  // Expose `--top-banners-offset` on :root so both <Navigation> (mobile
  // topbar) and <main> can read it. Without this, the var set inline on
  // <main> doesn't reach the topbar, which lives in a sibling subtree.
  useEffect(() => {
    document.documentElement.style.setProperty('--top-banners-offset', `${topBannerOffsetPx}px`)
    return () => {
      document.documentElement.style.removeProperty('--top-banners-offset')
    }
  }, [topBannerOffsetPx])

  const mainContent = (
    <SuspenseWrapper type={`nested`} className={cn(isNavigating && 'hidden')}>
      <div className={cn(`grow`, isNavigating && 'opacity-50')}>
        <LicenceBanner />
        <Outlet />
      </div>
    </SuspenseWrapper>
  )

  return (
    <>
      <Navigation />
      <MaintenanceOverrideBanner visible={showMaintenanceBanner} bannerIndex={0} />
      <PrefetchProgressBar bannerIndex={maintenanceOffset + (showStatusBanner ? 1 : 0)} />
      <BackgroundSyncIndicator
        visible={isBackgroundSyncing}
        bannerIndex={maintenanceOffset + Number(showStatusBanner) + Number(showPrefetchBanner)}
      />
      <OfflineBanner bannerIndex={maintenanceOffset} />
      <main
        className={cn(
          'relative flex flex-col pt-[calc(var(--mobileToBarHeight)+var(--safe-area-inset-top)+var(--top-banners-offset,0px))]',
          'pb-[calc(var(--bottomTabBarHeight)+var(--safe-area-inset-bottom))]',
          isNative ? 'h-svh' : 'min-h-svh',
          'md:ml-[var(--sidebar-width)] md:min-h-svh md:pb-0 md:pt-[var(--top-banners-offset,0px)]',
          isNative && 'md:h-auto',
        )}
        style={
          {
            '--top-banners-offset': `${topBannerOffsetPx}px`,
          } as CSSProperties
        }
      >
        {isNative ? (
          <div
            ref={scrollContainerRef}
            className="flex flex-1 flex-col overflow-y-auto md:overflow-visible"
            {...handlers}
          >
            <PullToRefreshIndicator
              state={pullState}
              contentRef={contentRef}
              indicatorRef={indicatorRef}
              iconWrapperRef={iconWrapperRef}
            >
              {mainContent}
            </PullToRefreshIndicator>
          </div>
        ) : (
          mainContent
        )}
      </main>
      <BottomTabBar />
      <MobileFab />
      <PushPromptCard />
      <SlowConnectionPrompt />
      {isSpecial && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-[2147483647] border border-amber-400/70 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.45),inset_0_0_10px_rgba(251,191,36,0.18),0_0_10px_rgba(251,191,36,0.18)]"
        />
      )}
      {isNavigating && <LoadingIndicator type="nested" />}
      {/* EnvironmentTag was a fixed bottom-right pill showing
          "D - <version>" / "S - <version>" — useful when the app had no
          other version surface, but now the build version is visible in
          the settings drawer footer (AppVersion). The floating pill was
          obscuring content in the bottom-right corner (sat on top of the
          mobile FAB and bottom tab bar) and duplicated info, so it's
          dropped. Re-mount here if a per-env color signal is needed. */}
    </>
  )
}
